import { FAUX_PAS_CORPUS, itemsByDifficulty, } from './corpus';
import { timing, variantForLevel } from './params';
const sessionMemory = { shown: new Set() };
export function resetSessionMemory() {
    sessionMemory.shown.clear();
}
function pickItemForDifficulty(difficulty) {
    const tiers = [difficulty, difficulty - 1, difficulty + 1, difficulty - 2, difficulty + 2];
    for (const t of tiers) {
        const pool = itemsByDifficulty(t).filter((i) => !sessionMemory.shown.has(i.id));
        if (pool.length > 0) {
            const choice = pool[Math.floor(Math.random() * pool.length)];
            sessionMemory.shown.add(choice.id);
            return choice;
        }
    }
    // Fallback : reset et reprends au tier demandé.
    sessionMemory.shown.clear();
    const all = itemsByDifficulty(difficulty);
    if (all.length === 0) {
        const fallback = FAUX_PAS_CORPUS[Math.floor(Math.random() * FAUX_PAS_CORPUS.length)];
        sessionMemory.shown.add(fallback.id);
        return fallback;
    }
    const choice = all[Math.floor(Math.random() * all.length)];
    sessionMemory.shown.add(choice.id);
    return choice;
}
export function makeStimulus(trialIdx, difficulty, _level) {
    const tier = Math.max(1, Math.min(5, Math.round(difficulty)));
    const item = pickItemForDifficulty(tier);
    return {
        trialIdx,
        difficulty: tier,
        item,
    };
}
export function createFauxPasDetectionAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        pickedYesNo: null,
        pickedWhoIdx: null,
        pickedWhyIdx: null,
        level,
        variant: variantForLevel(level),
        readingUnlocked: false,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function resolveTrial(resp) {
        if (resolver) {
            const r = resolver;
            resolver = null;
            rejecter = null;
            r(resp);
        }
    }
    return {
        mount() {
            // Idempotent — rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'reading',
                    stim,
                    feedback: null,
                    pickedYesNo: null,
                    pickedWhoIdx: null,
                    pickedWhyIdx: null,
                    readingUnlocked: false,
                });
                timers.push(setTimeout(() => {
                    update({ readingUnlocked: true });
                }, timing.readingMinMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        markReadingDone() {
            if (state.subPhase !== 'reading')
                return;
            if (!state.readingUnlocked)
                return;
            update({ subPhase: 'asking_yesno' });
            trialStart = Date.now();
        },
        handleYesNo(picked) {
            // Permettre une transition implicite reading → asking_yesno si le user
            // clique directement sur Oui/Non après le délai minimum de lecture.
            if (state.subPhase === 'reading') {
                if (!state.readingUnlocked)
                    return;
                update({ subPhase: 'asking_yesno' });
                trialStart = Date.now();
            }
            if (state.subPhase !== 'asking_yesno')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            update({ pickedYesNo: picked });
            const item = stim.item;
            if (picked === 'no') {
                // Pas de bourde détectée → correct ssi vraiment contrôle.
                const correct = !item.hasFauxPas;
                const rt = Date.now() - trialStart;
                resolveTrial({
                    pickedYesNo: 'no',
                    pickedWhoIdx: null,
                    pickedWhyIdx: null,
                    correct,
                    reactionTimeMs: rt,
                });
                return;
            }
            // picked === 'yes'
            if (!item.hasFauxPas) {
                // Fausse alarme : l'utilisateur croit voir une bourde sur un contrôle.
                const rt = Date.now() - trialStart;
                resolveTrial({
                    pickedYesNo: 'yes',
                    pickedWhoIdx: null,
                    pickedWhyIdx: null,
                    correct: false,
                    reactionTimeMs: rt,
                });
                return;
            }
            // Vraie bourde annoncée → on passe à qui + pourquoi.
            update({ subPhase: 'asking_who_why' });
        },
        handleWhoWhy(whoIdx, whyIdx) {
            if (state.subPhase !== 'asking_who_why')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const item = stim.item;
            const whoLabel = item.whoChoices[whoIdx] ?? null;
            const whoOk = whoLabel !== null && whoLabel === item.whoCommitted;
            const whyOk = item.whyCorrectIdx !== null && whyIdx === item.whyCorrectIdx;
            const correct = item.hasFauxPas && whoOk && whyOk;
            const rt = Date.now() - trialStart;
            update({ pickedWhoIdx: whoIdx, pickedWhyIdx: whyIdx });
            resolveTrial({
                pickedYesNo: 'yes',
                pickedWhoIdx: whoIdx,
                pickedWhyIdx: whyIdx,
                correct,
                reactionTimeMs: rt,
            });
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
        resetSession() {
            resetSessionMemory();
        },
    };
}
//# sourceMappingURL=adapter.js.map